if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        this.cookieName = 'sleektheme:popup';

        window.FoxTheme.DOMready(this.handleOpen.bind(this));

        window.FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '.btn-apply-coupon-code',
          handler: async (e, target) => {
            e.preventDefault();
            const code = target.dataset.code;
            if (code) this.copyToClipboard(code, target);
          },
        });
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.pendingOpen?.abort();
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();
        // However it opens (trigger or teaser), nothing still pending may open it again.
        this.pendingOpen?.abort();
      }

      handleOpen() {
        if (Shopify.designMode) return;

        if (this.isAlreadyOpened()) {
          return false;
        }

        this.pendingOpen = new AbortController();
        const { signal } = this.pendingOpen;
        const open = () => this.openWhenNothingElseIsOpen(signal);

        switch (this.triggerOpen) {
          case 'delay':
            this.triggerAfterFirstInteraction(5000, open, signal);
            break;
          case 'scroll_down':
            this.triggerAfterScroll(0.2, open, signal);
            break;
          default:
            open();
            break;
        }
      }

      isAlreadyOpened() {
        const savedData = FoxTheme.utils.getStorage(this.cookieName);
        return Boolean(savedData && savedData.opened);
      }

      triggerAfterFirstInteraction(delay, trigger, signal) {
        const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
        const onInteraction = () => {
          events.forEach((type) => window.removeEventListener(type, onInteraction));
          const timer = setTimeout(trigger, delay);
          signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
        };
        events.forEach((type) => window.addEventListener(type, onInteraction, { passive: true, signal }));
      }

      // ratio: share of the scrollable height, measured on each scroll because lazy content changes the page height.
      triggerAfterScroll(ratio, trigger, signal) {
        const onScroll = () => {
          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollable <= 0 || window.scrollY < scrollable * ratio) return;

          window.removeEventListener('scroll', onScroll);
          trigger();
        };
        window.addEventListener('scroll', onScroll, { passive: true, signal });
      }

      // Every modal and drawer is a ModalComponent: `open` while shown, then `inert` without `hidden` while its
      // closing transition runs. The body classes are the scroll lock (released only after the last modal's focus
      // cleanup), the search dropdown on the search page and the header dropdowns; .pswp is the product lightbox.
      hasOpenOverlay() {
        if (
          document.querySelector(
            'body:is(.modal-show, .modal-showing, .predictive-search-open, .has-dropdown-menu), .pswp'
          )
        ) {
          return true;
        }

        return Array.from(document.querySelectorAll('[open], [inert]:not([hidden])')).some(
          (element) => element !== this && element instanceof ModalComponent
        );
      }

      openWhenNothingElseIsOpen(signal) {
        const tryOpen = () => {
          if (signal.aborted || this.hasOpenOverlay()) return;

          this.pendingOpen.abort();
          if (!this.isAlreadyOpened()) this.show();
        };

        if (!this.hasOpenOverlay()) {
          tryOpen();
          return;
        }

        // The timeout lets a closing modal's own cleanup run first (handleAfterHide defers it the same way), so it
        // can't release the focus trap and the scroll lock the popup is about to take.
        let timer;
        const observer = new MutationObserver(() => {
          clearTimeout(timer);
          timer = setTimeout(tryOpen);
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
        observer.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['open', 'hidden'],
          subtree: true,
        });
        signal.addEventListener(
          'abort',
          () => {
            observer.disconnect();
            clearTimeout(timer);
          },
          { once: true }
        );
      }

      copyToClipboard(code, button) {
        navigator.clipboard.writeText(code);
        button.innerText = button.dataset.copiedText;
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}
